import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { SkedMark } from "@/components/SkedMark";
import { PropertyChip } from "@/components/ui/property-chip";
import { TooltipProvider } from "@/components/ui/tooltip";
import { buildSkedHref } from "@shared/leadgen/cta-rules";
import { MobileNav } from "./MobileNav";
import { SidebarNav } from "./SidebarNav";

interface AppShellProps {
  children?: ReactNode;
  eyebrow?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /**
   * Currently connected GA4 property. Rendered as a prominent context bar
   * above the eyebrow so the user always knows which property the report
   * reflects — with a quick "Change" shortcut.
   */
  property?: {
    propertyName: string;
    accountName: string | null;
  } | null;
  /** Optional override of the runtime APP_ENV (server-side render). */
  appEnv?: string;
}

export function AppShell({
  children,
  eyebrow,
  title,
  subtitle,
  actions,
  property,
  appEnv,
}: AppShellProps) {
  return (
    <TooltipProvider delayDuration={150} skipDelayDuration={300}>
      <div className="isolate flex min-h-dvh bg-neutral-25 antialiased">
        <SidebarNav appEnv={appEnv} />
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-10 border-b border-neutral-950/5 bg-white/85 backdrop-blur">
            {/* Mobile/tablet top bar (lg:hidden):
                - Hamburger (always) on the left → opens the slide-in drawer
                - Property name (sm+) in the middle if there's room
                - SkedMark brand anchor (always, links to /) on the right
              Desktop (lg+): the SidebarNav owns nav + brand; the PropertyChip
              renders only at lg below. The page-title block is a non-sticky
              sibling so only this property header stays pinned on scroll. */}
            <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:hidden">
              <MobileNav property={property ?? null} />
              <div className="hidden min-w-0 flex-1 items-center justify-center gap-3 px-3 text-sm sm:flex">
                {property ? (
                  <>
                    <span className="truncate font-display font-semibold text-neutral-950">
                      {property.propertyName}
                    </span>
                    {property.accountName ? (
                      <span className="hidden truncate text-neutral-500 md:inline">
                        · {property.accountName}
                      </span>
                    ) : null}
                  </>
                ) : null}
              </div>
              <Link
                to="/"
                aria-label="Traffic Snapshot — back to landing page"
                className="inline-flex shrink-0 items-center justify-center rounded-md focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
              >
                <SkedMark aria-hidden="true" className="size-8" />
              </Link>
            </div>
            {property ? (
              <div className="hidden lg:block">
                <PropertyChip
                  propertyName={property.propertyName}
                  accountName={property.accountName}
                />
              </div>
            ) : null}
          </header>
          <div className="flex flex-col gap-4 px-4 py-5 sm:flex-row sm:items-start sm:justify-between sm:px-6 lg:px-8 lg:py-6">
            <div className="min-w-0">
              {eyebrow ? (
                <>
                  {/* SKE-3427 — page-level eyebrow migrated from
                   *  legacy `.eyebrow-caps` (11px Gotham UPPERCASE) to
                   *  the new `.eyebrow` canon (17px Quarto italic
                   *  sentence case). The short blurple accent bar
                   *  between eyebrow and title is preserved per the
                   *  Sked design canon (Hugh's preference per
                   *  feedback_section_eyebrow_accent memory). */}
                  <p className="font-serif text-[17px] italic font-normal text-purple-700">
                    {eyebrow}
                  </p>
                  <span
                    aria-hidden="true"
                    className="mt-1 block h-0.5 w-6 rounded-full bg-purple-500"
                  />
                </>
              ) : null}
              <h1 className="mt-2 text-2xl font-bold tracking-tight text-neutral-950 text-balance">
                {title}
              </h1>
              {subtitle ? (
                <p className="mt-2 max-w-[72ch] text-sm text-neutral-600 text-pretty">{subtitle}</p>
              ) : null}
            </div>
            {actions ? (
              <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
            ) : null}
          </div>
          <main className="flex-1 px-4 pb-8 sm:px-6 lg:px-8 lg:pb-10">{children}</main>
          <AppFooter />
        </div>
      </div>
    </TooltipProvider>
  );
}

const FOOTER_BRAND_HREF = buildSkedHref({
  medium: "app_footer",
  campaign: "brand_attribution",
});

export function AppFooter() {
  return (
    <footer className="border-t border-neutral-950/5 px-4 py-6 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-neutral-500">
        <p>
          Built by the team behind social media management tool{" "}
          <a
            href={FOOTER_BRAND_HREF}
            target="_blank"
            rel="noreferrer"
            className="font-semibold text-purple-700 hover:text-purple-900 focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
          >
            Sked Social
          </a>
          .
        </p>
        <nav aria-label="Legal" className="flex items-center gap-5">
          <a
            href="https://skedsocial.com/terms-of-service"
            target="_blank"
            rel="noreferrer"
            className="text-purple-500 hover:text-purple-700 focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
          >
            Terms
          </a>
          <a
            href="https://skedsocial.com/privacy-policy"
            target="_blank"
            rel="noreferrer"
            className="text-purple-500 hover:text-purple-700 focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
          >
            Privacy
          </a>
        </nav>
      </div>
    </footer>
  );
}
